/**
 * Custom hook for role-based permissions
 * Provides permission checks based on user roles
 */

import { useAuthStore } from '../stores/auth.store';
import type { Role } from '../interfaces/apiInterfaces/roles';

export type UserRole = 'System Admin' | 'Content Creator' | 'Graphic Designer' | 'Delete';

const SUPPORTED_ROLES: UserRole[] = ['System Admin', 'Content Creator', 'Graphic Designer', 'Delete'];

/**
 * Check if user has a specific role
 */
const hasRole = (userRoles: Role[], roleName: UserRole): boolean => {
  return userRoles.some(role => role.role_name === roleName);
};

/**
 * Check if user has at least one supported role
 */
const hasSupportedRole = (userRoles: Role[]): boolean => {
  return userRoles.some(role => SUPPORTED_ROLES.includes(role.role_name as UserRole));
};

/**
 * Check if user is System Admin
 */
const isSystemAdmin = (userRoles: Role[]): boolean => {
  return hasRole(userRoles, 'System Admin');
};

/**
 * Check if user is Content Creator
 */
const isContentCreator = (userRoles: Role[]): boolean => {
  return hasRole(userRoles, 'Content Creator');
};

/**
 * Check if user is Graphic Designer
 */
const isGraphicDesigner = (userRoles: Role[]): boolean => {
  return hasRole(userRoles, 'Graphic Designer');
};

/**
 * Check if user has Delete role
 */
const hasDeleteRole = (userRoles: Role[]): boolean => {
  return hasRole(userRoles, 'Delete');
};

/**
 * Custom hook to access permissions based on user roles
 */
export function usePermissions() {
  const { user } = useAuthStore();
  const userRoles = user?.roles || [];
  const hasSupported = hasSupportedRole(userRoles);

  // If user doesn't have any supported role, deny all permissions
  if (!hasSupported) {
    return {
      // Role checks
      isSystemAdmin: false,
      isContentCreator: false,
      isGraphicDesigner: false,
      hasDeleteRole: false,
      
      // Module visibility
      canViewUsers: false,
      canViewSections: false,
      canViewUploadJobs: false,
      canViewReviewPublish: false,
      
      // User management permissions
      canManageUsers: false,
      canManageSections: false,
      
      // Job permissions
      canUploadJobs: false,
      canReUploadJobs: false,
      
      // Question permissions
      canPublishQuestions: false,
      canDeleteUnpublishedQuestions: false,
      canDeletePublishedQuestions: false,
      canDeleteQuestions: false,
    };
  }

  // Helper to check if user has any of the specified roles (OR logic)
  const hasAnyRole = (roles: UserRole[]): boolean => {
    return roles.some(role => hasRole(userRoles, role));
  };

  return {
    // Role checks
    isSystemAdmin: isSystemAdmin(userRoles),
    isContentCreator: isContentCreator(userRoles),
    isGraphicDesigner: isGraphicDesigner(userRoles),
    hasDeleteRole: hasDeleteRole(userRoles),
    
    // Module visibility
    canViewUsers: isSystemAdmin(userRoles), // System Admin only
    canViewSections: hasAnyRole(['System Admin', 'Content Creator']), // System Admin only
    canViewUploadJobs: hasAnyRole(['System Admin', 'Content Creator']), // System Admin or Content Creator
    canViewReviewPublish: hasAnyRole(['System Admin', 'Content Creator']), // System Admin or Content Creator
    
    // User management permissions
    canManageUsers: isSystemAdmin(userRoles), // System Admin only
    canManageSections: hasAnyRole(['System Admin', 'Content Creator']), // System Admin only
    
    // Job permissions
    canUploadJobs: hasAnyRole(['System Admin', 'Content Creator']), // System Admin or Content Creator
    canReUploadJobs: true, // All roles can re-upload (Graphic Designer needs this for fixing assets)
    
    // Question permissions
    canPublishQuestions: hasAnyRole(['System Admin', 'Content Creator']), // System Admin or Content Creator
    canDeleteUnpublishedQuestions: hasAnyRole(['System Admin', 'Content Creator']), // System Admin or Content Creator
    canDeletePublishedQuestions: isSystemAdmin(userRoles), // Only Admin can delete published
    canDeleteQuestions: hasDeleteRole(userRoles), // User must have "Delete" role
  };
}
